import { Color } from '@tmh-website/shared/colors';
import { ButtonThemeColor } from '@tmh-website/ui/Button';
import { DropdownThemeColor } from '@tmh-website/ui/Dropdown';
import { InfoTextButtonThemeColor } from '../InfoTextButton/InfoTextButton.theme';

const ensureAllFormElementThemesAvailable = <T extends FormElementThemeColor[]>(
  array: T & ([FormElementThemeColor] extends [T[number]] ? unknown : 'Invalid')
) => array;

export const formElementThemeColors = ensureAllFormElementThemesAvailable([
  Color.Blue_500_Ultramarine,
  Color.Blue_1000_Deep,
  Color.Green_1000_Energy,
  Color.Green_900,
  Color.Neutral_0_White,
  Color.Neutral_200_OffWhite,
  Color.Neutral_300,
  Color.Neutral_400,
  Color.Neutral_500,
  Color.Neutral_600,
  Color.Neutral_700,
  Color.Neutral_800,
  Color.Neutral_900,
  Color.Neutral_950_TechDarkGrey,
  Color.Neutral_1000_Black,
  Color.Purple_1000_Technology,
]);

export type FormElementThemeColor =
  | Color.Blue_500_Ultramarine
  | Color.Blue_1000_Deep
  | Color.Green_900
  | Color.Green_1000_Energy
  | Color.Neutral_0_White
  | Color.Neutral_200_OffWhite
  | Color.Neutral_300
  | Color.Neutral_400
  | Color.Neutral_500
  | Color.Neutral_600
  | Color.Neutral_700
  | Color.Neutral_800
  | Color.Neutral_900
  | Color.Neutral_950_TechDarkGrey
  | Color.Neutral_1000_Black
  | Color.Purple_1000_Technology;

interface FormElementTheme {
  background: {
    normal: Color;
    disabled: Color;
  };
  surface: {
    normal: Color;
    hover: Color;
    active: Color;
    disabled: Color;
  };
  border: {
    normal: Color;
    hover: Color;
    active: Color;
    disabled: Color;
  };
  checked: {
    normal: Color;
    hover: Color;
    active: Color;
    disabled: Color;
    text: Color;
  };
  rangeTrack: {
    background: Color;
    active: Color;
    disabled: Color;
  };
  text: Color;
  dropdownThemeColor: DropdownThemeColor;
  buttonThemeColor: ButtonThemeColor;
  infoTextButtonThemeColor: InfoTextButtonThemeColor;
}

export const formElementThemes: { [key in FormElementThemeColor]: FormElementTheme } = {
  [Color.Blue_1000_Deep]: {
    background: {
      normal: Color.Blue_1000_Deep,
      disabled: Color.Blue_800,
    },
    surface: {
      normal: Color.Blue_900,
      hover: Color.Blue_800,
      active: Color.Blue_700,
      disabled: Color.Blue_800,
    },
    border: {
      normal: Color.Blue_800,
      hover: Color.Blue_700,
      active: Color.Blue_600,
      disabled: Color.Blue_900,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Blue_800,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Blue_900,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Blue_900,
    buttonThemeColor: Color.Blue_1000_Deep,
    infoTextButtonThemeColor: Color.Blue_900,
  },

  /**
   * This theme variant has been added WITHOUT designs as a copy of the `Color.Blue_1000_Deep` with small adjustments.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-262
   */
  [Color.Blue_500_Ultramarine]: {
    background: {
      normal: Color.Blue_1000_Deep,
      disabled: Color.Blue_800,
    },
    surface: {
      normal: Color.Blue_900,
      hover: Color.Blue_800,
      active: Color.Blue_700,
      disabled: Color.Blue_800,
    },
    border: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_700,
      active: Color.Blue_600,
      disabled: Color.Blue_900,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Blue_800,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Blue_900,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Blue_900,
    buttonThemeColor: Color.Blue_1000_Deep,
    infoTextButtonThemeColor: Color.Blue_900,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme, based on the TMH Styleguide.
   *
   * @see https://mobilityhouse.atlassian.net/browse/WEBSITE23-374
   */
  [Color.Green_1000_Energy]: {
    background: {
      normal: Color.Green_1000_Energy,
      disabled: Color.Neutral_200_OffWhite,
    },
    surface: {
      normal: Color.Neutral_200_OffWhite,
      hover: Color.Neutral_300,
      active: Color.Neutral_400,
      disabled: Color.Neutral_200_OffWhite,
    },
    border: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_400,
      active: Color.Neutral_500,
      disabled: Color.Neutral_300,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_400,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_400,
      active: Color.Blue_200,
      disabled: Color.Neutral_300,
    },
    text: Color.Neutral_200_OffWhite,
    dropdownThemeColor: Color.Neutral_200_OffWhite,
    buttonThemeColor: Color.Green_1000_Energy,
    infoTextButtonThemeColor: Color.Neutral_200_OffWhite,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme, based on the TMH Styleguide.
   *
   * @see https://mobilityhouse.atlassian.net/browse/WEBSITE23-374
   */
  [Color.Green_900]: {
    background: {
      normal: Color.Green_900,
      disabled: Color.Neutral_200_OffWhite,
    },
    surface: {
      normal: Color.Neutral_200_OffWhite,
      hover: Color.Neutral_300,
      active: Color.Neutral_400,
      disabled: Color.Neutral_200_OffWhite,
    },
    border: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_400,
      active: Color.Neutral_500,
      disabled: Color.Neutral_300,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_400,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_400,
      active: Color.Blue_200,
      disabled: Color.Neutral_300,
    },
    text: Color.Blue_1000_Deep,
    dropdownThemeColor: Color.Neutral_200_OffWhite,
    buttonThemeColor: Color.Green_900,
    infoTextButtonThemeColor: Color.Neutral_200_OffWhite,
  },

  [Color.Neutral_0_White]: {
    background: {
      normal: Color.Neutral_0_White,
      disabled: Color.Neutral_200_OffWhite,
    },
    surface: {
      normal: Color.Neutral_200_OffWhite,
      hover: Color.Neutral_300,
      active: Color.Neutral_400,
      disabled: Color.Neutral_200_OffWhite,
    },
    border: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_400,
      active: Color.Neutral_500,
      disabled: Color.Neutral_300,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_400,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_400,
      active: Color.Blue_200,
      disabled: Color.Neutral_300,
    },
    text: Color.Blue_1000_Deep,
    dropdownThemeColor: Color.Neutral_200_OffWhite,
    buttonThemeColor: Color.Neutral_0_White,
    infoTextButtonThemeColor: Color.Neutral_200_OffWhite,
  },
  [Color.Neutral_200_OffWhite]: {
    background: {
      normal: Color.Neutral_0_White,
      disabled: Color.Neutral_200_OffWhite,
    },
    surface: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_400,
      active: Color.Neutral_500,
      disabled: Color.Neutral_300,
    },
    border: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_400,
      active: Color.Neutral_500,
      disabled: Color.Neutral_300,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_400,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Neutral_300,
    },
    text: Color.Blue_1000_Deep,
    dropdownThemeColor: Color.Neutral_0_White,
    buttonThemeColor: Color.Neutral_200_OffWhite,
    infoTextButtonThemeColor: Color.Neutral_400,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_300]: {
    background: {
      normal: Color.Neutral_300,
      disabled: Color.Neutral_200_OffWhite,
    },
    surface: {
      normal: Color.Neutral_200_OffWhite,
      hover: Color.Neutral_100,
      active: Color.Neutral_0_White,
      disabled: Color.Neutral_200_OffWhite,
    },
    border: {
      normal: Color.Neutral_100,
      hover: Color.Neutral_0_White,
      active: Color.Green_500_Success,
      disabled: Color.Neutral_300,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_200_OffWhite,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_200_OffWhite,
      active: Color.Blue_200,
      disabled: Color.Neutral_300,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_200_OffWhite,
    buttonThemeColor: Color.Neutral_300,
    infoTextButtonThemeColor: Color.Neutral_100,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_400]: {
    background: {
      normal: Color.Neutral_400,
      disabled: Color.Neutral_300,
    },
    surface: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_200_OffWhite,
      active: Color.Neutral_100,
      disabled: Color.Neutral_300,
    },
    border: {
      normal: Color.Neutral_200_OffWhite,
      hover: Color.Neutral_100,
      active: Color.Neutral_0_White,
      disabled: Color.Neutral_400,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_300,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_200_OffWhite,
      active: Color.Blue_200,
      disabled: Color.Neutral_400,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_300,
    buttonThemeColor: Color.Neutral_400,
    infoTextButtonThemeColor: Color.Neutral_200_OffWhite,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_500]: {
    background: {
      normal: Color.Neutral_500,
      disabled: Color.Neutral_400,
    },
    surface: {
      normal: Color.Neutral_400,
      hover: Color.Neutral_300,
      active: Color.Neutral_200_OffWhite,
      disabled: Color.Neutral_400,
    },
    border: {
      normal: Color.Neutral_300,
      hover: Color.Neutral_200_OffWhite,
      active: Color.Neutral_100,
      disabled: Color.Neutral_500,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_400,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_300,
      active: Color.Blue_200,
      disabled: Color.Neutral_500,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_400,
    buttonThemeColor: Color.Neutral_500,
    infoTextButtonThemeColor: Color.Neutral_300,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_600]: {
    background: {
      normal: Color.Neutral_600,
      disabled: Color.Neutral_500,
    },
    surface: {
      normal: Color.Neutral_500,
      hover: Color.Neutral_400,
      active: Color.Neutral_300,
      disabled: Color.Neutral_500,
    },
    border: {
      normal: Color.Neutral_400,
      hover: Color.Neutral_300,
      active: Color.Neutral_200_OffWhite,
      disabled: Color.Neutral_600,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_500,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_400,
      active: Color.Blue_200,
      disabled: Color.Neutral_600,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_500,
    buttonThemeColor: Color.Neutral_600,
    infoTextButtonThemeColor: Color.Neutral_400,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_700]: {
    background: {
      normal: Color.Neutral_700,
      disabled: Color.Neutral_600,
    },
    surface: {
      normal: Color.Neutral_600,
      hover: Color.Neutral_500,
      active: Color.Neutral_400,
      disabled: Color.Neutral_600,
    },
    border: {
      normal: Color.Neutral_500,
      hover: Color.Neutral_400,
      active: Color.Neutral_300,
      disabled: Color.Neutral_700,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_600,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Neutral_700,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_600,
    buttonThemeColor: Color.Neutral_700,
    infoTextButtonThemeColor: Color.Neutral_500,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_800]: {
    background: {
      normal: Color.Neutral_800,
      disabled: Color.Neutral_700,
    },
    surface: {
      normal: Color.Neutral_700,
      hover: Color.Neutral_600,
      active: Color.Neutral_500,
      disabled: Color.Neutral_700,
    },
    border: {
      normal: Color.Neutral_600,
      hover: Color.Neutral_500,
      active: Color.Neutral_400,
      disabled: Color.Neutral_800,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_700,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_400,
      active: Color.Blue_200,
      disabled: Color.Neutral_800,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_700,
    buttonThemeColor: Color.Neutral_800,
    infoTextButtonThemeColor: Color.Neutral_600,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_900]: {
    background: {
      normal: Color.Neutral_900,
      disabled: Color.Neutral_800,
    },
    surface: {
      normal: Color.Neutral_800,
      hover: Color.Neutral_700,
      active: Color.Neutral_600,
      disabled: Color.Neutral_800,
    },
    border: {
      normal: Color.Neutral_700,
      hover: Color.Neutral_600,
      active: Color.Neutral_500,
      disabled: Color.Neutral_900,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_800,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Neutral_900,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_800,
    buttonThemeColor: Color.Neutral_900,
    infoTextButtonThemeColor: Color.Neutral_500,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_950_TechDarkGrey]: {
    background: {
      normal: Color.Neutral_950_TechDarkGrey,
      disabled: Color.Neutral_900,
    },
    surface: {
      normal: Color.Neutral_1000_Black,
      hover: Color.Neutral_900,
      active: Color.Neutral_800,
      disabled: Color.Neutral_900,
    },
    border: {
      normal: Color.Neutral_900,
      hover: Color.Neutral_800,
      active: Color.Neutral_700,
      disabled: Color.Neutral_1000_Black,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_900,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Neutral_900,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_1000_Black,
    buttonThemeColor: Color.Neutral_950_TechDarkGrey,
    infoTextButtonThemeColor: Color.Neutral_900,
  },

  /**
   * This theme variant has been added WITHOUT designs using the AI-generated color scheme,
   * based on the previously defined color variants.
   *
   * @see https://mobilityhouse.atlassian.net/browse/EW-329
   */
  [Color.Neutral_1000_Black]: {
    background: {
      normal: Color.Neutral_1000_Black,
      disabled: Color.Neutral_900,
    },
    surface: {
      normal: Color.Neutral_900,
      hover: Color.Neutral_800,
      active: Color.Neutral_700,
      disabled: Color.Neutral_900,
    },
    border: {
      normal: Color.Neutral_900,
      hover: Color.Neutral_800,
      active: Color.Neutral_700,
      disabled: Color.Neutral_1000_Black,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Neutral_900,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Neutral_900,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Neutral_1000_Black,
    buttonThemeColor: Color.Neutral_950_TechDarkGrey,
    infoTextButtonThemeColor: Color.Neutral_900,
  },

  [Color.Purple_1000_Technology]: {
    background: {
      normal: Color.Purple_1000_Technology,
      disabled: Color.Purple_800,
    },
    surface: {
      normal: Color.Purple_900,
      hover: Color.Purple_800,
      active: Color.Purple_700,
      disabled: Color.Purple_800,
    },
    border: {
      normal: Color.Purple_900,
      hover: Color.Blue_800,
      active: Color.Blue_700,
      disabled: Color.Purple_900,
    },
    checked: {
      normal: Color.Blue_500_Ultramarine,
      hover: Color.Blue_600,
      active: Color.Blue_700,
      disabled: Color.Blue_800,
      text: Color.Neutral_0_White,
    },
    rangeTrack: {
      background: Color.Neutral_500,
      active: Color.Blue_200,
      disabled: Color.Purple_900,
    },
    text: Color.Neutral_0_White,
    dropdownThemeColor: Color.Purple_900,
    buttonThemeColor: Color.Blue_1000_Deep,
    infoTextButtonThemeColor: Color.Blue_1000_Deep,
  },
};
